import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API_BASE } from "../lib/api";
import { LOGO_URL } from "../lib/brand";
import { ShieldCheck, Leaf, Loader2, Sparkles, Eye, EyeOff, MapPin } from "lucide-react";
import { commodityDisplay } from "../lib/mandiDict";

const T = {
  हिंदी: { passport: "फसल बैच पासपोर्ट", organic: "प्राकृतिक रूप से उगाया — कोई रासायनिक स्प्रे दर्ज नहीं", verified: "पूर्ण खेती रिकॉर्ड सत्यापित", farm: "फार्म", field: "खेत", village: "गाँव", state: "राज्य", soil: "मिट्टी", sowing: "बुवाई", farmer: "किसान", timeline: "खेती टाइमलाइन", about: "इस फसल के बारे में", aiNote: "AI द्वारा तैयार सामान्य जानकारी", uses: "उपयोग", nutrition: "पोषण / औषधीय गुण", storage: "भंडारण", verifiedBy: "Agro Madat द्वारा सत्यापित", generated: "जारी", tagline: "किसान का सच्चा साथी" },
  English: { passport: "Crop Batch Passport", organic: "Naturally grown — no chemical sprays recorded", verified: "Full cultivation record verified", farm: "Farm", field: "Field", village: "Village", state: "State", soil: "Soil", sowing: "Sowing", farmer: "Farmer", timeline: "Cultivation timeline", about: "About this crop", aiNote: "AI-generated general information", uses: "Uses", nutrition: "Nutrition / medicinal value", storage: "Storage", verifiedBy: "Verified by Agro Madat", generated: "Generated", tagline: "The farmer's true companion" },
};

export default function PublicTrace() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [lang, setLang] = useState("हिंदी");
  const [showFarmer, setShowFarmer] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/public/trace/${id}`).then((r) => (r.ok ? r.json() : Promise.reject())).then(setData).catch(() => setErr(true));
  }, [id]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-500 p-6 text-center">Passport not found or expired.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-green-600" /></div>;

  const p = data.passport;
  const x = T[lang];
  const L = lang === "हिंदी" ? "hi" : "en";
  const disp = commodityDisplay(p.crop, lang);
  const about = p.about;

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white text-slate-800">
      <div className="max-w-lg mx-auto p-5">
        <div className="flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Agro Madat" className="h-10 w-10 rounded-xl object-contain bg-white shadow" />
            <div><p className="font-bold">Agro Madat</p><p className="text-xs text-slate-500">{x.passport}</p></div>
          </div>
          <div className="flex rounded-full bg-white shadow border overflow-hidden text-xs font-semibold">
            {["हिंदी", "English"].map((l) => (
              <button key={l} onClick={() => setLang(l)} className={`px-3 py-1.5 ${lang === l ? "bg-green-600 text-white" : "text-slate-600"}`} data-testid={`trace-lang-${l}`}>{l}</button>
            ))}
          </div>
        </div>

        <div className="rounded-2xl bg-white/90 backdrop-blur shadow-lg border p-5" data-testid="public-passport">
          <div className="flex items-center gap-4 mb-4">
            {disp.img ? <img src={disp.img} alt="" className="h-20 w-20 rounded-2xl object-cover" /> : <div className="h-20 w-20 rounded-2xl bg-green-100 flex items-center justify-center text-4xl">{disp.emoji || p.emoji}</div>}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold leading-tight">{disp.main}</h1>
              <p className="text-sm text-slate-500">{disp.sub || p.localName} {p.variety ? `· ${p.variety}` : ""}</p>
              <span className="text-[11px] font-mono text-slate-400">{data.traceId}</span>
            </div>
          </div>

          <div className={`rounded-xl p-3 text-sm flex items-center gap-2 mb-4 ${p.organic ? "bg-green-100 text-green-700" : "bg-emerald-50 text-emerald-700"}`}>
            <ShieldCheck className="h-5 w-5 shrink-0" />{p.organic ? x.organic : x.verified}
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="col-span-2 flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
              <div><p className="text-xs text-slate-400">{x.farmer}</p><p className="font-medium">{showFarmer ? (p.farmerName || "—") : "•••••"}</p></div>
              <button onClick={() => setShowFarmer((v) => !v)} className="text-slate-400" data-testid="trace-toggle-farmer">{showFarmer ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
            </div>
            <Field label={x.farm} value={p.farm} />
            <Field label={x.field} value={`${p.field || "—"} · ${p.areaAcres || "—"} ac`} />
            <Field label={x.village} value={[p.village, p.district].filter(Boolean).join(", ")} icon={MapPin} />
            <Field label={x.state} value={p.state} />
            <Field label={x.soil} value={p.soilType} />
            <Field label={x.sowing} value={(p.sowingDate || "").slice(0, 10) || "—"} />
          </div>

          {p.inputs?.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1"><Leaf className="h-3 w-3" /> {x.timeline}</p>
              <div className="space-y-1.5">
                {p.inputs.map((inp, i) => (
                  <div key={i} className="text-sm flex justify-between border-b last:border-0 py-1.5"><span>{inp.type} — {inp.title}</span><span className="text-slate-400 text-xs">{inp.date}</span></div>
                ))}
              </div>
            </div>
          )}
          <p className="text-[11px] text-slate-400 mt-4">{x.verifiedBy} · {x.generated} {new Date(p.generatedAt).toLocaleDateString("en-IN")}</p>
        </div>

        {about && (about[`summary_${L}`] || about.summary_en) && (
          <div className="rounded-2xl bg-white/90 backdrop-blur shadow-lg border p-5 mt-4" data-testid="public-about">
            <p className="font-bold flex items-center gap-1.5 mb-1"><Sparkles className="h-4 w-4 text-green-600" />{x.about}</p>
            {about.scientific && <p className="text-xs italic text-slate-500 mb-2">{about.scientific}</p>}
            <p className="text-sm mb-3">{about[`summary_${L}`] || about.summary_en}</p>
            {(about[`uses_${L}`] || about.uses_en) && <AboutRow label={x.uses} value={about[`uses_${L}`] || about.uses_en} />}
            {(about[`nutrition_${L}`] || about.nutrition_en) && <AboutRow label={x.nutrition} value={about[`nutrition_${L}`] || about.nutrition_en} />}
            {(about[`storage_${L}`] || about.storage_en) && <AboutRow label={x.storage} value={about[`storage_${L}`] || about.storage_en} />}
            <p className="text-[11px] text-slate-400 mt-3">ℹ️ {x.aiNote}</p>
          </div>
        )}

        <p className="text-center text-xs text-slate-400 mt-4">{x.tagline}</p>
      </div>
    </div>
  );
}

function Field({ label, value, icon: Icon }) {
  return <div><p className="text-xs text-slate-400 flex items-center gap-1">{Icon && <Icon className="h-3 w-3" />}{label}</p><p className="font-medium">{value || "—"}</p></div>;
}
function AboutRow({ label, value }) {
  return <div className="mb-2"><p className="text-xs font-semibold text-slate-500">{label}</p><p className="text-sm text-slate-700">{value}</p></div>;
}
